import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	Form,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
	useEditFieldOptionRegionDetailMutation,
	useGetFieldOptionRegionDetailQuery,
	useCreateFieldOptionRegionDetailMutation,
} from '@/api/field-option/fieldOption';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { FormType } from '@/api/enum/formType.enum';
import { useGetUser } from '@/hooks/useGetUser';
import { PermissionEnum } from '@/api/enum/common.enum';

interface Props {
	currentId?: string;
	formType: string;
	type: number;
	parentId: string;
}

const EditRegionDialog = (props: Props) => {
	const { currentId, formType, type, parentId } = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [editFieldOptionRegionDetail] =
		useEditFieldOptionRegionDetailMutation();
	const [createFieldOptionRegionDetail] =
		useCreateFieldOptionRegionDetailMutation();
	const { userInfo } = useGetUser();

	useEffect(() => {
		if (!open) methods.reset();
	}, [open]);

	const { data: details } = useGetFieldOptionRegionDetailQuery(
		currentId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !open || !currentId,
		}
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		code: Yup.string().required(t('errorMessage.required')),
		nameEn: Yup.string().required(t('errorMessage.required')),
		nameZh: Yup.string().required(t('errorMessage.required')),
	});
	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		if (details) {
			methods.reset(details.data);
		}
	}, [details]);

	const handleClose = () => {
		setOpen(false);
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response =
			formType == FormType.create
				? await createFieldOptionRegionDetail({
						...data,
						type: type,
						parentId: parentId,
				  })
				: await editFieldOptionRegionDetail({
						...data,
						type: type,
						id: currentId,
						parentId: parentId,
				  });
		if ('data' in response) {
			enqueueSnackbar(
				formType == FormType.create
					? t('snackbar.createFieledUnitSuccess')
					: t('snackbar.updateFieledUnitSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			handleClose();
		}
	};

	const handleDialog = async () => {
		setOpen(true);
	};

	return (
		<>
			{userInfo?.permissionList?.includes(
				PermissionEnum.OptionManagementCreate
			) && formType == FormType.create ? (
				<Button
					endIcon={<AddCircleOutlineOutlinedIcon />}
					onClick={() => {
						handleDialog();
					}}
					variant='contained'
				>
					{t('field.newOption')}
				</Button>
			) : userInfo?.permissionList?.includes(
					PermissionEnum.OptionManagementEdit
			  ) ? (
				<IconButton
					type='button'
					color='secondary'
					onClick={() => {
						handleDialog();
					}}
				>
					<EditOutlinedIcon />
				</IconButton>
			) : (
				''
			)}

			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
				onKeyDown={(e) => {
					if (e.key === 'Tab') {
						e.stopPropagation();
					}
				}}
			>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<DialogTitle variant='h5'>
						{formType == FormType.create
							? t('field.newOption')
							: t('field.updateOption')}
					</DialogTitle>
					<IconButton
						aria-label='close'
						onClick={handleClose}
						sx={{
							position: 'absolute',
							right: 8,
							top: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<Close />
					</IconButton>
					<DialogContent>
						<Typography variant='body2' sx={{ marginBottom: '12px' }}>
							{formType == FormType.create
								? t('field.updateOptionTextAdd')
								: t('field.updateOptionText')}
						</Typography>
						<FormProvider {...methods}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12}>
									<AppTextField
										control={methods.control}
										name='code'
										label={t('field.code') + '*'}
									/>
								</Grid>
								<Grid size={12}>
									<AppTextField
										control={methods.control}
										name='nameEn'
										label={t('field.en') + '*'}
									/>
								</Grid>
								<Grid size={12}>
									<AppTextField
										control={methods.control}
										name='nameZh'
										label={t('field.zh') + '*'}
									/>
								</Grid>
							</Grid>
						</FormProvider>
					</DialogContent>
					<DialogActions>
						<Button
							sx={{ textTransform: 'none' }}
							onClick={() => handleClose()}
						>
							{t('button.cancel')}
						</Button>
						<Button
							type='submit'
							variant='contained'
							onClick={methods.handleSubmit(onSubmit)}
						>
							{t('button.confirm')}
						</Button>
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
};

export default EditRegionDialog;
